import { PermissionDto } from "../types/Role";
import { FileActions } from "./Enums";

export const GLOBALSETTINGS = {
  ESTTIMEZONE: "America/New_York",
  TO_LOCALE: "en-US",
  DATE_FORMAT: "MM/dd/yyyy",
  DATE_FORMAT_ISO: "yyyy-MM-dd",
  DATE_FORMAT_TIME: "MM/dd/yyyy hh:mm a",
  TIME_ONLY: "hh:mm a",
  DEFAULT_STATE: -1,
  DEFAULT_VALUE: "1000",
  FAILED_ADDITION: " Failed",
  MAX_ITEMS: 20,
  DEFAULT_PAGE: "/Home",
  MIN_GLOBAL_SEARCH: 2,
  DEFAULT_OTP_FIELD_LENGTH: 6,
  LIST_DETAIL_PAGE: "/listDetails",
  TWOFA_OTP_FIELD_LENGTH: 6,
  ISO_PATTERN: /^\d{4}-\d{2}-\d{2}T/,
  MAX_TIME_DIFF_MS: 60 * 1000,
  MAX_RETRIES: 3,
  RETRY_DELAY: 3000,
  // No HTTP timeout was configured anywhere, so a slow or unreachable dependency stalled the
  // UI indefinitely — the user saw a spinner that never ended.
  API_TIMEOUT_MS: 60 * 1000,
  // Axios `timeout` is a TOTAL response timeout, not an inactivity one, so a blanket
  // API_TIMEOUT_MS would abort a large blob/zip stream mid-download. These get their own,
  // far looser bound: still finite, but well past any legitimate transfer.
  DOWNLOAD_TIMEOUT_MS: 10 * 60 * 1000,
  // api.ipify.org is a third party on the login critical path and `fetch` has no default
  // timeout. Both callers already treat a failed lookup as non-fatal, so fail fast.
  IP_LOOKUP_TIMEOUT_MS: 3000,
  BLINK_TIME: 7000,
  INVALID_FILENAME_CHARS_REGEXP: /[/\\:*?"<>|]/,
  INVALID_FILENAME_CHARS_TEXT: '\\/:*?"<>|',
  DESCRIPTION_LENGTH: 235,
  RECYCLEBIN_PAGE: "/Recyclebin",
};

export const PERMISSION_KEYS: (keyof PermissionDto)[] = [
  "canView",
  "canDownload",
  "canShareToUser",
  "canShareToRole",
  "canEdit",
  "canDelete",
  "canManagePermissions",
];

export const AUDITRACEACTIONS = {
  MOVEDFOLDER: "Folder Moved",
  MOVEDFILE: "File Moved",
  RESTORE: "Restore",
  UNLOCKUSER: "Unlock User",
  LOCKUSER: "Lock User",
  UNLOCKSESSION: "Unlock Session",
  LOCKSESSION: "Lock Session",
  Login: "Login",
  Logout: "Logout",
};

//* Audit Log "Source" filter values. The API routes on these strings
//* (AuditLogQryHandler compares Source against "SQLA"), so they must match exactly.
export const AUDIT_SOURCES = {
  PORTAL: "Portal",
  SQLA: "SQLA",
};

// A wide unbounded Audit Log range scans a huge volume, hanging or timing out the grid.
// A range wider than this many days is only allowed when it is scoped to specific user(s),
// which keeps the query cheap. Applies to BOTH sources: SQLA (rows in Hamaspik_Live_Logs)
// and Portal - the SQLA-only carve-out of Epic 80054 AC5 was dropped. The name keeps its
// SQLA_ prefix only so it still lines up with Constants.AuditLogSqlaMaxDays on the API side.
export const AUDITLOG_SQLA_MAX_DAYS = 3;

// Epic 80054 - the widest span the Activities grid will query over the navigation-trace log
// when it is NOT scoped to a user, i.e. when the Users filter is cleared and the query covers
// everyone. Same rule as AUDITLOG_SQLA_MAX_DAYS above, and both are stated in words by the
// shared GLOBALMESSAGES.AUDITLOG_SQLA_RANGE_TOO_WIDE - keep the three in step.
export const ACTIVITIES_MAX_DAYS = 3;

export const EDITRESOURCES = {
  EDIT_FOLDER: "Edit Folder / File",
  PUBLISH_FOLDER: "Publish Folder / File",
  UPDATE: "Update",
  PUBLISH: "Publish",
  CANCEL: "Cancel",
};

export const SEARCHBOX = {
  FOLDERLIST_PLACEHOLDER: "Search docs, tags, etc",
  DEBOUNCE_TIME: 1000,
  TEXT_PLACEHOLDER: "Search",
};

export const ACTIVITIES = {
  DEBOUNCE_TIME: 500,
};

export const Messages = {
  MAXIMUMCOUNT_LIMIT: 99,
};

export const SYSTEMSETTINGS = {
  RESOURCE_PAGINATION: "ResourcePagination",
  DEFAULT_PAGINATION: 100,
  USERNAVIGATION_TRACETIME: "UserNavigationTraceTime",
  AUDITTRACE_PAGINATION: "AuditNavigationTracePagination",
  PORTAL_CATEGORY: "Portal",
  RECENT_VIEWEDITEMS: "RecentViewedItems",
  OTP_FIELD_LENGTH: "OTPFieldLength",
  MANAGEUSERS_PAGINATION: "ManageUsersPagination",
  MANAGESESSIONS_PAGINATION: "ManageSessionsPagination",
  NOTIFICATONS_PAGINATION: "NotificationsPagination",
  ATTACHMENT_EXTENSIONS: "AllowedAttachmentExtensions",
  ATTACHMENT_SIZE: "AllowedAttachmentSize",
  ATTACHMENT_LIMIT: "AllowedAttachmentLimit",
  FILEUPLOADMAXSIZE: "FileUploadMaxSize",
  FILECATEGORIES: "FileCategories",
  USER_ACTIVITY_DURATION: "UserInactivityDuration",
};

export const DEFAULT_SYSTEMSETTINGS = {
  ResourcePagination: 100,
  UserNavigationTraceTime: 1,
  AuditNavigationTracePagination: 100,
  RecentViewedItems: 4,
  NotificationsPagination: 15,
  AllowedAttachmentExtensions: "pdf,jpg,jpeg,png,mp4,avi,txt",
  AllowedAttachmentSize: 10,
  AllowedAttachmentLimit: 5,
};

export const AUDITNAVIGATIONTRACES = {
  AUDIT_STORAGE_KEY: "auditnavigationtrace_logs",
  RECENT_VIEWED_FILES: "audit_recent_viewed_files",
  RECENT_VIEWED_FOLDERS: "audit_recent_viewed_folders",
};

export const ENTITYTYPE = {
  EMAIL: "Email",
  INBOX: "Inbox",
  SENT: "Sent",
  RECALL: "Recalled",
};

export const KEYS = {
  SESSIONID: "sessionId",
};

export const SEVERITIES = {
  SUCCESS: "success",
  ERROR: "error",
  WARNING: "warning",
  INFO: "info",
} as const;

export const PASSWORDSYSTEMSETTINGS = {
  PASSWORD_BLACKLIST: "Password Blacklist",
  PASSWORD_COMPLEXITY_REQUIREMENTS: "Password Complexity Requirements",
  PASSWORD_LENGTH: "Password Length",
  PROHIBITED_PASSWORD_CHAR: "Prohibited Password Characters",
  DEFAULT_PASSWORD_LENGTH: 8,
} as const;

export const SIGNUPMODE = {
  SIGNUP_WITH_PASSWORD: "SIGNUP-WITH-PASSWORD",
  SIGNUP_WITH_OTP: "SIGNUP-WITH-OTP",
  SIGNUP_WITH_AUTHAPP: "SIGNUP-WITH-AUTHAPP",
  SIGNUP_WITH_PASSWORD_OR_OTP: "SIGNUP-WITH-PASSWORD-OR-OTP",
  SIGNUP_WITH_PASSWORD_OR_AUTHAPP: "SIGNUP-WITH-PASSWORD-OR-AUTHAPP",
  SIGNUP_WITH_PASSWORD_AND_OTP: "SIGNUP-WITH-PASSWORD-AND-OTP",
  SIGNUP_WITH_PASSWORD_AND_AUTHAPP: "SIGNUP-WITH-PASSWORD-AND-AUTHAPP",
  SIGNUP_PAGE_TITLE: "Just a few quick things to get started",
} as const;

export const MODULEINFO = {
  MODULENAME: "HR-EmployeeList",
};

export const RouteKeywords = {
  FileExplorer: ["listdetails", "home"],
  AuditLog: ["audit-log"],
  ErrorLog: ["error-log"],
  // Must precede "Manage": the "/manageSettings" URL also contains "manage", so this
  // entry has to match first for getModuleName() to resolve it to the ManageSettings module.
  ManageSettings: ["managesettings"],
  Manage: ["manage"],
  Login: ["signin"],
  Signup: ["signup"],
  Recyclebin: ["recyclebin"],
  Activities: ["activities"],
  Notifications: ["notification"],
  Messages: ["messages"],
  UserProfile: ["user-profile"],
  IpWhiteList: ["white-listed-ip"],
  // Order matters: both entries below must precede "PostIFMyForms", which is a bare substring
  // match ("postif") that would otherwise also match these more specific URLs.
  PostIFList: ["postif/list"],
  // Any "/hiring/postif/<something>" other than "/list" is the create/edit form
  // (new or :id) - gated by its own module, separate from the MyForms list.
  PostIFForm: ["postif/"],
  PostIFMyForms: ["postif"],
  // Epic 80427 seeds PageUrl as singular /workflow/... and that path must match the React route.
  // Keep each full segment here. A bare "workflow" keyword would match every one of these URLs.
  // /workflow/run is not a module. It is a Builder capability; MenuGuard checks Builder CanEdit.
  // Instances and My Work are one screen and one grant. /workflow/my-work is the seeded
  // PageUrl; it resolves here and renders the same screen with My Work in focus (Epic 80431).
  WorkflowInstances: ["workflow/my-work", "workflow/instances"],
  WorkflowBuilder: ["workflow/run", "workflow/builder"],
  WorkflowLibrary: ["workflow/library"],
} as const;

export const ModuleNames = {
  FileExplorer: "EnterprisePortal-FileExplorer",
  AuditLog: "EnterprisePortal-AuditLog",
  Recyclebin: "EnterprisePortal-RecycleBin",
  ErrorLog: "EnterprisePortal-ErrorLog",
  Manage: "EnterprisePortal-Manage",
  Login: "EnterprisePortal-Login",
  Signup: "EnterprisePortal-Signup",
  Activities: "EnterprisePortal-Activities",
  UsersActivities: "EnterprisePortal-Activities-AllUsers",
  Messages: "EnterprisePortal-Messages",
  Notifications: "EnterprisePortal-Notifications",
  UserImpersonation: "EnterprisePortal-UserImpersonation",
  ManageSettings: "EnterprisePortal-ManageSettings",
  Contacts: "EnterprisePortal-Contacts",
  Reassign: "EnterprisePortal-Messages-Reassign",
  UserProfile: "EnterprisePortal-UserProfile",
  IpWhiteList: "EnterprisePortal-IPWhitelisting",
  PostIFMyForms: "EnterprisePortal-PostIF-MyForms",
  PostIFList: "EnterprisePortal-PostIF-List",
  PostIFForm: "EnterprisePortal-PostIF-Form",
  WorkflowLibrary: "EnterprisePortal-Workflow-Library",
  WorkflowBuilder: "EnterprisePortal-Workflow-Builder",
  WorkflowInstances: "EnterprisePortal-Workflow-Instances",
  WorkflowMyWork: "EnterprisePortal-Workflow-MyWork",
} as const;

export const ExcludeSideNavModuleSet = new Set<string>([
  ModuleNames.Login,
  ModuleNames.Signup,
  ModuleNames.UsersActivities,
  ModuleNames.Messages,
]);

export const LeftSideNavModuleSet = new Set<string>([
  ModuleNames.FileExplorer,
  ModuleNames.Activities,
  ModuleNames.Recyclebin,
  ModuleNames.ErrorLog,
  ModuleNames.AuditLog,
  ModuleNames.Manage,
  ModuleNames.ManageSettings,
  ModuleNames.IpWhiteList,
  ModuleNames.PostIFMyForms,
  ModuleNames.PostIFList,
  ModuleNames.WorkflowLibrary,
  ModuleNames.WorkflowBuilder,
  ModuleNames.WorkflowInstances,
  ModuleNames.WorkflowMyWork,
]);

/** Static-nav permission bypasses are for the local dev server and Jest only.
 *
 * `react-scripts build` pins NODE_ENV=production for every deployed artifact — including QA and
 * staging, which go through `build:test` — so these can never be true in a deployed bundle. The
 * only non-production contexts are `react-scripts start` (development) and Jest (test). Set
 * REACT_APP_ENABLE_STATIC_UI_BYPASS=false in a local .env to force them off while developing
 * against a seeded database. */
const STATIC_UI_BYPASS_ENABLED =
  process.env.NODE_ENV !== "production" &&
  process.env.REACT_APP_ENABLE_STATIC_UI_BYPASS !== "false";

/** TODO Epic 79822: remove this bypass once PostIF modules are seeded in System_Modules + Portal DB.
 * Gated so it can never ship the permission bypass to prod (PR 30712 review). */
export const POSTIF_STATIC_UI_BYPASS = STATIC_UI_BYPASS_ENABLED;

export const POSTIF_STATIC_UI_NAV_LINKS = [
  {
    title: "Post Interview List",
    url: "/hiring/postif",
    moduleName: ModuleNames.PostIFMyForms,
  },
] as const;

/** TODO Epic 80427: remove this bypass once the Workflow modules are seeded in both databases. */
export const WORKFLOW_STATIC_UI_BYPASS = STATIC_UI_BYPASS_ENABLED;

export const WORKFLOW_STATIC_UI_NAV_LINKS = [
  {
    title: "Workflow Library",
    url: "/workflow/library",
    moduleName: ModuleNames.WorkflowLibrary,
    icon: "/workflow-library.svg",
  },
  {
    title: "Builder",
    url: "/workflow/builder",
    moduleName: ModuleNames.WorkflowBuilder,
    icon: "/workflow-builder.svg",
  },
  {
    title: "Test / Run",
    url: "/workflow/run",
    moduleName: ModuleNames.WorkflowBuilder,
    icon: "/workflow-test-run.svg",
  },
  {
    title: "Instances & My Work",
    url: "/workflow/instances",
    moduleName: ModuleNames.WorkflowInstances,
    icon: "/workflow-instances.svg",
  },
] as const;

export type ModuleName = (typeof ModuleNames)[keyof typeof ModuleNames];

export const permissionMap: Record<keyof typeof FileActions, string> = {
  Download: "canDownload",
  AddNew: "canEdit",
  Edit: "canEdit",
  Publish: "isPublish",
  UndoPublish: "undoPublish",
  ShareToUser: "canShareToUser",
  ShareToRole: "canShareToRole",
  MakeACopy: "canEdit",
  MoveToBin: "canDelete",
};

export const permissionModuleMap: Record<keyof typeof FileActions, string> = {
  Download: "canDownload",
  AddNew: "canEdit",
  Edit: "canEdit",
  Publish: "canEdit",
  UndoPublish: "canEdit",
  ShareToUser: "canEdit",
  ShareToRole: "canEdit",
  MakeACopy: "canEdit",
  MoveToBin: "canDelete",
};

export const ERRORMESSAGES = {
  APIErrorLogging: "Error logging to API",
};

export const REGEX = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE: /^[- +()0-9]+$/,
  USERNAME: /^[a-zA-Z0-9_.]+$/,
  REGEX_ESCAPE_PATTERN: /[.*+?^${}()|[\]\\]/g,
  REGEX_ESCAPE_REPLACEMENT: "\\$&",
};

export const dataGridRowSpacing = 5;
export const dataGridHeaderHeight = 45;
export const dataGridRowHeight = 45;

export const VISIBLE_DIGITS = 3;

// Portal_Messages.MessageSubject is varchar(100) - the compose UI must enforce the same
// limit, otherwise a longer subject only fails at the insert with a 500.
export const MESSAGE_SUBJECT_MAX_LENGTH = 100;

export const SIGNINRESPONSES = {
  VALID_USER: "Valid User",
  INVALID_USER: "Invalid User",
  ACCOUNT_LOCKED: "Account Locked",
  INCORRECT_PASSWORD: "Incorrect Password",
  SESSION_LOCKED: "Session Locked",
  PASSWORD_NOT_SET: "Password Not Set",
  TFA_NOT_CONFIGURED: "2FA not configured",
  ROLE_NOT_ASSIGNED: "Role not assigned",
  ALREADY_REGISTRED_WITH_PRIMARY_ACCOUNT: "Already registered with primary account",
  IP_NOT_WHITELISTED: "IP Not Whitelisted",
  // Returned when the user's Portal_Users.ActiveUntil window has passed. Must match the
  // string emitted by sProc_Portal_Users_Verify_SignIn exactly, or the sign-in pages fall
  // through their result chains and show no message at all.
  ACCOUNT_EXPIRED: "Account Expired"
};

// Why an account is locked. The API reports both reasons as result "Account Locked" and
// distinguishes them with the isAdminLocked flag; a failed-attempt lockout can be cleared by a
// self-service password reset, an admin lock cannot. Held as one value rather than two booleans
// so the "locked" and "which reason" states can never disagree.
export const ACCOUNTLOCKKIND = {
  NONE: "",
  FAILED_ATTEMPTS: "FailedAttempts",
  ADMIN: "Admin",
  // Locked, but the API did not say why (e.g. the OTP / 2FA sign-in paths report the lock as a
  // bare result string with no DTO). Falls back to the original neutral message so we never
  // promise a self-service unlock we cannot deliver, nor send a self-recoverable user to an admin.
  UNSPECIFIED: "Unspecified",
} as const;

export type AccountLockKind = (typeof ACCOUNTLOCKKIND)[keyof typeof ACCOUNTLOCKKIND];

export const PASSWORDCHANGEMODE = {
  FORGOT_PASSWORD: "Forgot",
  SETUP_PASSWORD: "Setup",
  CHANGE_PASSWORD: "Change Password",
  SET_PASSWORD: "Set Password",
  FORGOT_PASSWORD_TITLE: "Forgot password",
  SETUP_PASSWORD_TITLE: "Set up password",
  SET_NEW_PASSWORD_TITLE: "Set New Password",
  CANNOT_REUSE_PASSWORD: "You cannot reuse passwords",
  NOMATCH_PASSWORD : "The entered password does not match the current password.",
};

export const OTPMODE = {
  SMS: "SMS",
  EMAIL: "EMAIL",
  TWOFA: "2FA",
  VERIFY: "OTPVERIFY",
  SEND: "OTPSEND",
};
export const OTPSENTMODE = {
  PHONE: "Phone",
  EMAIL: "Email",
};

export const VERIFICATIONTYPE = {
  OTP: "OTP",
  SENDOTP: "SENDOTP",
  TWOFA: "2FA",
  PASSWORD: "PASSWORD",
  RESET_PASSWORD: "RESET-PASSWORD",
};
export const OTPFOR = {
  SIGNIN: "SignIn",
  SIGNUP: "SignUp",
  RESETPASSWORD: "ResetPassword",
};
export const COMMONCONSTANTS = {
  MODULENAME: "RequestFormHR",
  MOUSEDOWN: "mousedown",
  SIMPLEPOPOVER: "simple-popover",
  SIGNINGWORKS: "How Signing In works?",
  DESCRIPTIONWORKS: "Description on how it works",
  FURTHERQUERY: "If you have any further queries,",
  CLICKHERE: "Click Here",
  FILLOUTFORM: " to fill out the form.",
  GOTIT: "Got it",
  HELPFORM: "Help Desk Request Form",
  NAME: "Name",
  EMAILORPHONE: "Email",
  EMAILPLACEHOLDER: "Enter Email",
  EMAILORPHONEPLACEHOLDER: "Enter Email/Phone",
  PHONEPLACEHOLDER: "Enter Phone",
  ISSUE: "What is the Issue?",
  FIRSTNAMEPLACEHOLDER: "Enter First Name",
  LASTNAMEPLACEHOLDER: "Enter Last Name",
  SELECTISSUE: "Please select issue",
  ADDITIONALDETAILS: "Additional Details",
  ADDITIONALDETAILSPLACEHOLDER: "Enter Additional Details",
  SEND: "Send",
  LOADING: "Loading...",
  SETPASSWORD: "Set yourself up with a Password",
  NEEDPASSWORD: "Do I need a password, ",
  WHY: "Why?",
  WHYPASSWORD: "Why you need a password?",
  UNAUTHORIZEDACCOUNT: "It prevents unauthorized access to your account",
  ENTER_VALID_EMAIL: "Please enter valid Email",
  ENTER_VALID_PHONE: "Please enter valid Phone",
  DEFAULT_ISSUETYPE_VALUE: "General Question",
  ISSUE_TYPE_GENERAL_QUESTION: "General Question",
  TRUE_VALUE: "TRUE",
  FALSE_VALUE: "FALSE",
  ROLE_NOT_ASSIGNED: "Do not have role assigned",
  ROLE_NOT_MAPPED: "Role Not Mapped",
  ROLE_NOT_FOUND: "Role Not Found",
  USER_NOT_FOUND: "User Not Found",
  ROLE_MAPPED: "Role Mapped",
  PORTALSOURCE: "Portal",
  LOG_SOURCE: "Portal",
  SSO: "SSO",
  INACTIVITY:"inactivity",
  TO: "to"
};

export const ITEM_TITLES = {
  DOCUMENTS: "Documents",
  PICTURES: "Pictures",
  VIDEOS: "Videos",
  AUDIOS: "Audios",
};

export const FILE_CATEGORIES = {
  DOCUMENT: "document",
  IMAGE: "image",
  VIDEO: "video",
  AUDIO: "audio",
};

export const USERINFO = {
  SESSIONVALUE: "SessionValue",
};
export const SSOCALLBACK = {
  TOKEN: "token",
  USERID: "userId",
  USERNAME: "userName",
  FIRSTNAME: "firstName",
  LASTNAME: "lastName",
  RESPONSEMSG: "responseMessage",
  ROLENAMES: "roleNames",
  ISFIRSTTIMELOGIN: "isFirstTimeLogin",
};

export const IMPERSONATION_INFO = {
  ImpersonationId: "ImpersonationId",
  IsImpersonating: "IsImpersonating",
  ImpersonatorUserId: "ImpersonatedUserId",
  ImpersonatorUserName: "ImpersonatedUserName",
  ImpersonatorToken: "ImpersonatorToken",
  ImpersonatorRefreshToken: "ImpersonatorRefreshToken",
  ImpersonatorSessionValue: "ImpersonatorSessionValue",
  Impersonator: "Impersonator",
  ImpersonatedMessage: "ImpersonatedMessage",
  TargetUserName: "TargetUserName",
};

export const LOCALSTORAGENAME = {
  SESSIONVALUE: "SessionValue",
  ISSESSIONLOCKED: "IsSessionLocked",
  DEVICEID: "DeviceId",
  ISGOOGLELOGGEDIN: "IsGoogleLoggedin",
  ISAZURELOGGEDIN: "IsAzureLoggedin",
  SESSIONID: "SessionId",
  ROLENAMES: "RoleNames",
  REMEMBERME: "RememberMe",
  LOGOUTEVENT: "LogoutEvent",
  ISFIRSTTIMELOGIN: "IsFirstTimeLogin",
  PHONENUMBER: "PhoneNumber",
  EMAILADDRESS: "EmailAddress",
  INACTIVITY: "Inactivity",
  PENDINGTOAST: "PendingToast",
  GRIDCOLUMNVISIBILITY: "GridColumnVisibility",
  // Epic 82838: the Post Interview list's status/department filters, stored as `${this}:${userId}`.
  POSTIFHRFILTERS: "postif-hr-filters"
};

// Bug 78034: identifies each grid's "Manage columns" selection inside the single
// LOCALSTORAGENAME.GRIDCOLUMNVISIBILITY entry, so the choice survives page navigation.
// These strings are persisted - renaming one silently drops that grid's saved selection.
export const GRIDCOLUMNKEYS = {
  FOLDER_LIST: "folderList",
  RECYCLE_BIN: "recycleBin",
  ACTIVITIES: "activities",
  AUDIT_LOG: "auditLog",
  ERROR_LOG_FRONTEND: "errorLogFrontEnd",
  ERROR_LOG_API: "errorLogApi",
  IP_WHITELIST: "ipWhitelist",
  MANAGE_SETTINGS: "manageSettings",
  PORTAL_USERS: "portalUsers",
  PORTAL_USER_SESSIONS: "portalUserSessions",
  MESSAGES: "messages",
  POST_INTERVIEW_FORMS: "postInterviewForms"
};

export const AllowedForLoggedIn = ["General Question"];
export const AllowedForGuests = ["Signing Up", "Logging In"];

export const OTPSTATUS = {
  NOT_MATCHED: "OTP Not Matched",
  SENT: "OTP Sent",
  INVALID: "Invalid OTP or Session",
  EXPIRED: "OTP Expired",
  INCORRECT: "Incorrect OTP",
  MATCHED: "OTP Matched",
  SESSION_LOCKED: "Session Locked",
};
export const COOKIESNAME = {
  USERINPUT: "USERINPUT",
  USERSIGNUPINPUT: "USERSIGNUPINPUT",
  USERID: "USERID",
  REMEMBERME: "REMEMBERME",
};
export const TOKEN = {
  ACCESSTOKEN: "AccessToken",
  REFRESHTOKEN: "RefreshToken",
};
export const COLORS = ["#ff5722", "#2196f3", "#4caf50", "#ff9800", "#9c27b0"];

const API_URL = process.env.REACT_APP_API_URL;
const CLIENT_NAME = "PortalApp";
const REDIRECT_URI = `${window.location.origin}/SSOCallback`;
export const SSO_URL = {
  GOOGLE: `${API_URL}/api/signin-google?client=${CLIENT_NAME}&redirectUri=${REDIRECT_URI}&frontendRedirect=${window.location.origin}`,
  MICROSOFT: `${API_URL}/api/azure-login?client=${CLIENT_NAME}&redirectUri=${REDIRECT_URI}&frontendRedirect=${window.location.origin}`,
};

export const STATUS = {
  SUCCESS: "success",
  ERROR: "error",
} as const;

export const MENU_LABELS = {
  FILE_EXPLORER: "File Explorer",
  ADMIN: "Admin",
};
export const JOB_STATUS = {
  PENDING: "pending",
  ZIPPING: "zipping",
  COMPLETED: "completed",
  FAILED: "failed",
} as const;

export type JobStatus = (typeof JOB_STATUS)[keyof typeof JOB_STATUS];

export const DROPDOWN_CONFIG = {
  DISPLAY_LIMIT: 2,

  LABELS: {
    EMPTY: "Please Select",
    ALL: "All Users Selected",
  },
};

/**
 * System_Settings_New rows in the "PostIF" category (main DB), seeded by Epic 79819.
 * These are NOT in the Redux settings store — SettingsProvider only loads the
 * "Portal" category — so read them with usePifSettings, not useSettingValue.
 */
export const POSTIF_SETTINGS = {
  CATEGORY: "PostIF",
  RETRY_LIMIT: "Not-this-person retry limit",
  LOOKUP_RATE_LIMIT_PER_MINUTE: "Identity lookup rate limit per minute",
  ALLOW_WITHOUT_EMAIL: "Allow new hire without personal email",
  ALLOW_WITHOUT_CELL: "Allow new hire without cell",
  DIRECTOR_DERIVATION_METHOD: "Director derivation method",
} as const;

/** Fallbacks matching each row's seeded DefaultValue, for an unseeded environment. */
export const POSTIF_SETTING_DEFAULTS = {
  RETRY_LIMIT: 3,
  ALLOW_WITHOUT_EMAIL: false,
  ALLOW_WITHOUT_CELL: false,
} as const;

/**
 * Portal_PostIF.Flow values. The column is a free VARCHAR(20) with no constraint, and
 * the approval chain (Epic 79823) branches on this string — keep it in sync with the API.
 */
export const PIF_FLOW = {
  NEW_HIRE: "new-hire",
  EXISTING_ACTIVE: "existing-active",
  REHIRE: "rehire",
} as const;

/** `result` values returned by POST api/postif/identity-lookup. */
export const PIF_LOOKUP_RESULT = {
  EXISTING: "existing",
  NEW: "new",
  BLOCKED: "blocked",
} as const;

/** `subFlow` values accompanying an "existing" identity-lookup result. */
export const PIF_SUB_FLOW = {
  ACTIVE: "active",
  INACTIVE: "inactive",
} as const;

/**
 * HR position classifications the PostIF form branches on, as returned by
 * postif-position-rules. Real values from the main DB — not a UI-side taxonomy.
 */
export const PIF_CLASSIFICATION = {
  COMMUNITY_DSP: "Community-based DSP",
} as const;

/** DataType values on Portal_PostIFAttributes, driving how a dynamic field renders. */
export const PIF_FIELD_TYPE = {
  TEXT: "text",
  DATE: "date",
  DECIMAL: "decimal",
  OPTION: "option",
} as const;

/**
 * The approver deep link. Its final path segment is an opaque one-time token, so anything
 * deriving a display name from the last segment must special-case it — see
 * useAuditNavigationTrace, which would otherwise write the token itself into the audit trail.
 */
export const POSTIF_SIGN_ROUTE_PREFIX = "/hiring/postif/sign/";

/** Stable audit-trail page name for the deep-link signing screen. */
export const POSTIF_SIGN_TRACE_LABEL = "Post Interview Form Signing";

/**
 * Epic 80430 (decision 6.5): the workflow case share link, /wf/{RefNo}. Its last segment is the
 * case's RefNo, so useAuditNavigationTrace names it by this label instead; the Run screen's own
 * trace records which case was opened.
 */
export const WORKFLOW_SHARE_ROUTE_PREFIX = "/wf/";

/** Stable audit-trail page name for the share link (it redirects straight to the Run screen). */
export const WORKFLOW_SHARE_TRACE_LABEL = "Workflow Case Link";

/**
 * Audit-trail page names for routes whose URL slug does not read as the page name.
 * useAuditNavigationTrace otherwise names a page after its last path segment, which logged
 * "Manage" for Manage Users & Sessions, "ManageSettings" for Manage Settings and
 * "White Listed Ip" for the IP list (Bug 79916) — the first being ambiguous between the two
 * "Manage" pages. Keys are lower-cased pathnames; values match the sidebar the user clicked.
 */
export const AUDIT_TRACE_PAGE_NAMES: Record<string, string> = {
  "/manage": "Manage Users & Sessions",
  "/managesettings": "Manage Settings",
  "/white-listed-ip": "White-Listed IP List",
};
